import React, { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  createUserWithEmailAndPassword,
  signInAnonymously
} from 'firebase/auth';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { auth } from '../../api/firebase/config';
import { addStaffUser } from '../../api/firebase/firestore/user';
import { clientStaffRegisterSchema } from '../../api/firebase/firestore/user/validation';
import {
  blackText,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  sizeBelowTenMb,
  textMb,
  titleMb,
  w100
} from '../../constants/Location';
import { screenType } from '../../constants/enum';
import { throwError } from '../../modules/error';

import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import PasswordTextFiled from './PasswordTextField';

interface ClientRegisterStaffType {
  iconId: string;
  name: string;
  department: string;
  password: string;
  icon: File;
}

const styles = {
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginBottom: '16px'
  },
  button: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      mt: '12px'
    },
    mt: '16px'
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  w100
};

interface Props {
  screen: number;
}

const StaffAddCard: React.FC<Props> = (props) => {
  const { screen } = props;
  const { t } = useTranslation();
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<ClientRegisterStaffType>({
    resolver: yupResolver(clientStaffRegisterSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('name') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('department') === undefined ||
        '' ||
        Boolean(errors.name) ||
        Boolean(errors.password) ||
        Boolean(errors.department) ||
        Boolean(logoSizeError)
      );
    }
    return false;
  };

  useEffect(() => {
    try {
      if (params.get('anchor') === 'login') {
        if (!params.get('email')) {
          throw new Error();
        } else {
          setDefaultEmail(params.get('email')?.split('?').join('') ?? '');
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setOpen(true);
      setErrorText(t('registerBasic.linkError'));
      throwError(e);
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  const [previewIcon, setPreviewIcon] = useState('');
  const [s3FileIcon, setS3FileIcon] = useState<FileList | null>(null);
  const [logoSizeError, setIconSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3FileIcon && s3FileIcon.length > 0) {
      sizeBelowTenMb(s3FileIcon[0].size)
        ? setIconSizeError(t('validation.error.imageSize'))
        : setIconSizeError(null);
    } else {
      setIconSizeError(null);
    }
  }, [s3FileIcon]);

  const changeUploadLogoFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreviewIcon(window.URL.createObjectURL(blob));
      setS3FileIcon(file);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);

      const anonymous = await signInAnonymously(auth);

      if (anonymous) {
        const createdUser = await createUserWithEmailAndPassword(
          auth,
          defaultEmail,
          watch('password')
        );

        if (createdUser) {
          if (screen === screenType.client) {
            await addStaffUser(
              params.get('email'),
              createdUser,
              watch,
              s3FileIcon
            );
            navigate('/auth/invite/register/complete');
          }
        }
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('staff.add.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomImageUploader
                    {...field}
                    type="icon"
                    preview={previewIcon}
                    error={Boolean(errors.icon)}
                    otherError={logoSizeError}
                    helperText={errors.icon?.message}
                    onChange={(value: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(value);
                      changeUploadLogoFile(value);
                    }}
                  />
                )}
                control={control}
                name="icon"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText={t('form.name')}
                    requiredText={t('form.required')}
                    isRequired
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText={t('staff.add.department')}
                    placeholder={t('staff.add.departmentPlaceholder')}
                    requiredText={t('form.required')}
                    isRequired
                  />
                )}
                control={control}
                name="department"
              />
              <Controller
                render={({ field }) => (
                  <PasswordTextFiled
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText={t('form.password')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder=" "
                    id="password"
                    fullWidth
                  />
                )}
                control={control}
                name="password"
              />
              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[primaryButtonBackground, styles.button]}
                onClick={() => {
                  void (async () => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      if (logoSizeError) return;
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 15 }}>
                  {t('staff.add.button')}
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default StaffAddCard;
